// LoanUnitsDrawer.tsx
import {
  Drawer,
  Box,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";

const clickableCellStyle = {
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
};

async function fetchLoanUnits(loanNumber: string, token?: string | null) {
  const { data } = await axios.get(
    `${apiVariable}/getUnitPerLoanId/${loanNumber}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return data;
}

const LoanUnitsDrawer = ({
  loanId,
  loanNumber,
  open,
  onClose,
}: {
  loanId: string | undefined | null;
  loanNumber: string | undefined | null;
  open: boolean;
  onClose: () => void;
}) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const {
    data: loanUnits,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["loanUnits", loanId],
    queryFn: () => fetchLoanUnits(loanId || "", token),
    enabled: open && !!loanId,
  });

  const columns = [
    {
      field: "unitNumber",
      headerName: "Unit Number",
      width: 150,
      renderCell: (params: any) => (
        <Box sx={clickableCellStyle}>{params.value}</Box>
      ),
    },
    {
      field: "monthlyPayment",
      headerName: "Monthly Payment",
      width: 200,
      valueFormatter: (params: any) => `$${(params ?? 0).toFixed(2)}`,
    },
    {
      field: "remainingBalance",
      headerName: "Remaining Balance",
      width: 200,
      valueFormatter: (params: any) => `$${(params ?? 0).toFixed(2)}`,
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          top: 64,
          height: "calc(100% - 64px)",
        },
      }}
    >
      <Box sx={{ maxWidth: "100%", width: 600, p: 2 }}>
        <Button
          onClick={onClose}
          sx={{ display: { xs: "inline-flex", lg: "none" }, mb: 1 }}
        >
          Close units
        </Button>
        <Typography variant="h6">
          Units for Loan: {loanNumber || "N/A"}
        </Typography>
        {isLoading ? (
          <CircularProgress sx={{ mt: 2 }} />
        ) : isError ? (
          <Typography color="error" sx={{ mt: 2 }}>
            Error: {(error as Error).message}
          </Typography>
        ) : (
          <DataGrid
            rows={loanUnits || []}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            getRowId={(row) => row.unitNumber}
            sx={{ mt: 2 }}
            onCellClick={(params) => {
              if (params.field === "unitNumber") {
                if (params.row.truckId !== null) {
                  navigate(`/fleet/assets/truck/${params.row.truckId}`);
                } else if (params.row.trailerId !== null) {
                  navigate(`/fleet/assets/trailer/${params.row.trailerId}`);
                }
              }
            }}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default LoanUnitsDrawer;
